import type { Dispatch } from 'react';
import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import includes from 'lodash/includes';
import PropTypes from 'prop-types';

import ListingCardV3 from 'app/shared/modules/listing-cards/ListingCardV3';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import 'app/shared/modules/listing-cards/ListingWrapper.scss';

// Tracking & constants
import ImpressionTracking from 'app/shared/modules/ImpressionTracking';
import { ListingProvider } from './ListingCardProvider';
import type {
  SaveHomeTriggerLocation,
  SaveHomeTriggerObject,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import type { ListingType } from 'app/types';
import type { SimilarListingDetails } from 'app/types/similarListingDetails.type';
import constants from 'app/shared/constants/ExperimentConstants';
import type { AnalyticsState } from 'app/shared/flux/reducers/analytics';

/*
TODO HPWEB-6194:
1. convert to functional component
2. Add commute time tag to bigger listing card.
*/

export interface ListingWrapperProps {
  className?: string;
  commuteTime?: any;
  customRef?: any;
  dispatch: Dispatch<any>;
  isTreatingListingAsNotViewed?: boolean;
  keepMapLocation?: boolean;
  key: string;
  listing: ListingType | SimilarListingDetails;
  onClick: () => void;
  onContactClick?: () => void;
  setActiveMarkerOnHover: boolean;
  shouldLazyLoadListingCardPhotos: boolean;
  hideContactButton?: boolean;
  isCarouselCard?: boolean;
  wrapperAs?: 'li' | 'div';

  trackImpression: (listing: ListingType | SimilarListingDetails) => void;
  triggerLocation: SaveHomeTriggerLocation;
  triggerObject: SaveHomeTriggerObject;
  deferRender?: boolean;
  isApplyInstantlyExperiment: boolean;
}

interface ListingWrapperState {
  listingViewed: boolean;
}

class ListingWrapper extends React.Component<ListingWrapperProps, ListingWrapperState> {
  static displayName = 'ListingWrapper';
  static propTypes = {
    keepMapLocation: PropTypes.bool,
    shouldLazyLoadListingCardPhotos: PropTypes.bool,
    onClick: PropTypes.func,
    onContactClick: PropTypes.func,
    listing: PropTypes.object.isRequired,
    setActiveMarkerOnHover: PropTypes.bool,
    trackImpression: PropTypes.func,
    customRef: PropTypes.object,
    wrapperAs: PropTypes.oneOf(['li', 'div']),
  };

  static defaultProps = {
    isTreatingListingAsNotViewed: false,
    keepMapLocation: false,
    shouldLazyLoadListingCardPhotos: false,
    onClick: () => {},
    onContactClick: () => {},
    trackImpression: () => {},
    setActiveMarkerOnHover: true,
    customRef: null,
  };

  constructor(props: ListingWrapperProps) {
    super(props);
    const { isTreatingListingAsNotViewed, listing } = this.props;

    this.state = {
      listingViewed: isTreatingListingAsNotViewed
        ? false
        : includes(listing?.userItemTypes, 'viewed') || includes(listing?.userItemTypes, 'favorite'),
    };
  }

  shouldComponentUpdate(nextProps: ListingWrapperProps) {
    const { commuteTime, listing } = this.props;
    const thisPriceDisplay = listing.listingMinMaxPriceBeds.priceDisplay;
    const nextPriceDisplay = nextProps.listing.listingMinMaxPriceBeds.priceDisplay;

    if (listing.userItemTypes !== nextProps.listing?.userItemTypes) {
      return true;
    } else if (thisPriceDisplay !== nextPriceDisplay) {
      return true;
    } else if (nextProps.commuteTime !== commuteTime) {
      return true;
    } else {
      return false;
    }
  }

  handleMouseEnter = () => {
    const { dispatch, listing, setActiveMarkerOnHover } = this.props;
    if (setActiveMarkerOnHover) {
      dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(listing.maloneLotIdEncoded));
    }
  };

  handleMouseExit = () => {
    const { dispatch, setActiveMarkerOnHover } = this.props;
    if (setActiveMarkerOnHover) {
      dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(null));
    }
  };

  render() {
    const {
      className,
      listing,
      shouldLazyLoadListingCardPhotos,
      customRef,
      key,
      keepMapLocation,
      onClick,
      onContactClick,
      triggerLocation,
      triggerObject = 'property_card',
      deferRender,
      isApplyInstantlyExperiment,
      hideContactButton,
      isCarouselCard = false,
      wrapperAs = 'li',
    } = this.props;

    const { listingViewed } = this.state;

    if (!listing) {
      return null;
    }

    const WrapperElement = wrapperAs;

    return (
      <ListingProvider listing={listing} triggerLocation={triggerLocation} triggerObject={triggerObject}>
        <WrapperElement
          className={cx('ListingWrapper-container', className)}
          key={key}
          ref={customRef}
          id={listing.maloneLotIdEncoded}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseExit}
        >
          <ListingCardV3
            keepMapLocation={keepMapLocation}
            onClick={onClick}
            onContactClick={onContactClick}
            listingViewed={listingViewed}
            shouldLazyLoadListingCardPhotos={shouldLazyLoadListingCardPhotos}
            shouldDeferRender={deferRender}
            isApplyInstantlyExperiment={isApplyInstantlyExperiment}
            hideContactButton={hideContactButton}
            isCarouselCard={isCarouselCard}
          />
          <ImpressionTracking analyticsCallback={() => this.props.trackImpression(listing)} />
        </WrapperElement>
      </ListingProvider>
    );
  }
}

const mapStateToProps = (state: { analytics: AnalyticsState }) => ({
  isApplyInstantlyExperiment:
    state?.analytics?.runningExperiments?.[constants.HOTPADS_WEB_APPLY_INSTANTLY]?.isControl === false,
});

export default connect(mapStateToProps)(ListingWrapper);
